import { useCallback, useState } from "react";

import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";

import { OperatorType } from "@regal-voice/ui";
import { getPhonesEmails } from "Components/shared/SearchableDialPad/ContactSearch/queries";
import { useAudiencePathing } from "Pages/audience/useAudiencePathing";
import {
    formatPhoneNumber,
    VALID_PHONE_CHARACTERS_REGEX,
    INVALID_EMAIL_REGEX,
    INVALID_NAME_REGEX,
} from "Services/CommunicationService";

import { SpotlightOption } from "../types";
import { SpotlightAudienceItem } from "./SpotlightAudienceItem";

interface UseGetSearchAudiencesProps {
    onClose: () => void;
}

interface Profile {
    id: string;
    firstName: string;
    lastName: string;
    contactPhone: string;
    email: string;
    rvProperties: Record<string, any>;
    externalId: string;
    subBrand: string;
}

export function useGetSearchAudiences({ onClose }: UseGetSearchAudiencesProps) {
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { contactsBasePath } = useAudiencePathing();

    const [searchProfiles] = useLazyQuery(getPhonesEmails, {
        fetchPolicy: "network-only",
    });

    const getAudiences = useCallback(
        async (searchValue: string): Promise<SpotlightOption[]> => {
            if (!searchValue) {
                return [];
            }

            setLoading(true);
            try {
                // Determine search type based on input (similar to ProfileLookup)
                const searchBy = VALID_PHONE_CHARACTERS_REGEX.test(searchValue)
                    ? "contactPhone"
                    : INVALID_NAME_REGEX.test(searchValue)
                      ? "email"
                      : "contactName";

                const filterBy = [
                    {
                        key: searchBy === "contactName" ? "name" : searchBy,
                        value: searchValue,
                        operator: OperatorType.STARTS_WITH,
                    },
                ];

                // If searching by name and value could be an email, also search email
                if (searchBy === "contactName" && !INVALID_EMAIL_REGEX.test(searchValue)) {
                    filterBy.push({
                        key: "email",
                        value: searchValue,
                        operator: OperatorType.STARTS_WITH,
                    });
                }

                // Also search by external ID if it looks like it could be one
                if (searchBy === "contactName") {
                    filterBy.push({
                        key: "externalId",
                        value: searchValue,
                        operator: OperatorType.STARTS_WITH,
                    });
                }

                const { data } = await searchProfiles({
                    variables: {
                        queryParams: {
                            orderBy: [
                                {
                                    key: "updatedAt",
                                    value: "DESC",
                                },
                            ],
                            page: 0,
                            pageSize: 25,
                            filterBy,
                        },
                    },
                });

                const profiles = data?.getPhonesEmails?.items as Profile[];

                if (!profiles?.length) {
                    return [];
                }

                return [
                    {
                        label: "Audience",
                        options: profiles.map((profile) => {
                            // Build description with phone, email, and external ID
                            const descriptionParts = [
                                profile.contactPhone ? formatPhoneNumber(profile.contactPhone) : null,
                                profile.email,
                                profile.externalId,
                            ].filter(Boolean);
                            const description = descriptionParts.join(" | ");
                            const route = `${contactsBasePath}?profileId=${profile.id}`;

                            return {
                                disabled: false,
                                label: (
                                    <SpotlightAudienceItem
                                        name={`${profile.firstName} ${profile.lastName}`}
                                        description={description}
                                        searchValue={searchValue}
                                        route={route}
                                        onCallback={() => {
                                            navigate(route);
                                            onClose();
                                        }}
                                    />
                                ),
                                value: profile.id,
                            };
                        }),
                    },
                ];
            } catch {
                return [];
            } finally {
                setLoading(false);
            }
        },
        [onClose, navigate, searchProfiles, contactsBasePath]
    );

    return { getAudiences, loading };
}
